'use client'

import {
    downloadLedgerReceiptPdf,
    getLedgerEntry,
    LedgerEntryDetail,
    LedgerEntryType,
    sendLedgerReceiptEmail,
} from '@/lib/api/financial/ledger-api'
import { extractFriendlyFields, INTERNAL_TYPE_LABELS, resolveOperationLabel } from '@/lib/financial/ledger-translations'
import { useEffect, useState } from 'react'
import { MdArrowBack, MdEmail, MdPictureAsPdf, MdReceipt } from 'react-icons/md'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const TYPE_LABELS = INTERNAL_TYPE_LABELS as Record<LedgerEntryType, string>

const TYPE_COLORS: Record<LedgerEntryType, string> = {
    CREDIT_PAYMENT: 'badge-success',
    FEE: 'badge-warning',
    TRANSFER_OUT: 'badge-info',
    REFUND: 'badge-error',
    CHARGEBACK: 'badge-error',
    SPLIT_IN: 'badge-success',
    SPLIT_OUT: 'badge-info',
    ADJUSTMENT: 'badge-ghost',
}

const formatBrl = (cents: number, withSign = true): string => {
    const v = (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    if (!withSign) return v
    return cents > 0 ? `+${v}` : v
}

const formatDateTime = (iso: string | null | undefined): string =>
    iso ? new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—'

export default function LedgerDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [entry, setEntry] = useState<LedgerEntryDetail | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [downloading, setDownloading] = useState(false)
    const [showEmailForm, setShowEmailForm] = useState(false)
    const [recipientEmail, setRecipientEmail] = useState('')
    const [sendingEmail, setSendingEmail] = useState(false)

    const handleDownloadPdf = async () => {
        if (!entry) return
        setDownloading(true)
        try {
            await downloadLedgerReceiptPdf(entry.id)
        } catch (err: any) {
            toast.error(err?.message || 'Erro ao gerar PDF')
        } finally {
            setDownloading(false)
        }
    }

    const handleSendEmail = async () => {
        if (!entry) return
        if (!recipientEmail.trim() || !/^.+@.+\..+$/.test(recipientEmail)) {
            return toast.warning('Informe um email válido')
        }
        setSendingEmail(true)
        try {
            const res = await sendLedgerReceiptEmail(entry.id, { email: recipientEmail.trim() })
            if (res.success) {
                toast.success(`Comprovante enviado para ${recipientEmail.trim()}`)
                setShowEmailForm(false)
                setRecipientEmail('')
            } else {
                toast.error(res.error || 'Erro ao enviar')
            }
        } finally {
            setSendingEmail(false)
        }
    }

    useEffect(() => {
        if (!id) return
        ;(async () => {
            setLoading(true)
            try {
                const res = await getLedgerEntry(id)
                if (res.success && res.result) setEntry(res.result)
                else setError(res.error || 'Movimentação não encontrada')
            } finally {
                setLoading(false)
            }
        })()
    }, [id])

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="loading loading-spinner loading-lg text-secondary" />
            </div>
        )
    }

    if (error || !entry) {
        return (
            <div className="p-6 max-w-2xl mx-auto">
                <button className="btn btn-ghost btn-sm mb-4" onClick={() => navigate(-1)}>
                    <MdArrowBack /> Voltar
                </button>
                <div className="alert alert-error">{error || 'Movimentação não encontrada'}</div>
            </div>
        )
    }

    const raw = (entry.raw ?? {}) as any
    const operationLabel = resolveOperationLabel({
        type: entry.type,
        asaasType: raw.asaasType ?? raw.type ?? null,
        raw,
    })
    const friendlyFields = extractFriendlyFields(raw)

    return (
        <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
                    <MdArrowBack /> Voltar
                </button>
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-sm btn-outline gap-2"
                        onClick={handleDownloadPdf}
                        disabled={downloading}
                    >
                        {downloading ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : (
                            <MdPictureAsPdf />
                        )}
                        Baixar PDF
                    </button>
                    <button
                        className="btn btn-sm btn-secondary gap-2"
                        onClick={() => setShowEmailForm((v) => !v)}
                    >
                        <MdEmail /> Enviar por email
                    </button>
                </div>
            </div>

            {showEmailForm && (
                <div className="card bg-base-200 shadow-sm">
                    <div className="card-body p-4">
                        <p className="text-sm font-medium mb-2">Enviar comprovante para:</p>
                        <div className="flex gap-2">
                            <input
                                type="email"
                                className="input input-bordered input-sm flex-1"
                                placeholder="destinatario@exemplo.com"
                                value={recipientEmail}
                                onChange={(e) => setRecipientEmail(e.target.value)}
                                disabled={sendingEmail}
                                autoFocus
                            />
                            <button
                                className="btn btn-sm btn-ghost"
                                onClick={() => { setShowEmailForm(false); setRecipientEmail('') }}
                                disabled={sendingEmail}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-sm btn-secondary"
                                onClick={handleSendEmail}
                                disabled={sendingEmail || !recipientEmail.trim()}
                            >
                                {sendingEmail ? <span className="loading loading-spinner loading-xs" /> : 'Enviar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="card bg-base-100 shadow">
                <div className="card-body">
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-xl font-bold flex items-center gap-2">
                                <MdReceipt /> Movimentação #{entry.id}
                            </h1>
                            <p className="text-xs text-base-content/60 mt-1">
                                {formatDateTime(entry.occurredAt)}
                            </p>
                        </div>
                        <span className={`badge ${TYPE_COLORS[entry.type]}`}>
                            {TYPE_LABELS[entry.type]}
                        </span>
                    </div>

                    <div className="divider my-2" />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <Field label="Valor">
                            <span className={`font-mono font-bold text-lg ${entry.amount > 0 ? 'text-success' : entry.amount < 0 ? 'text-error' : ''}`}>
                                {formatBrl(entry.amount)}
                            </span>
                        </Field>
                        {entry.balanceAfter != null && (
                            <Field label="Saldo após">
                                <span className="font-mono">{formatBrl(entry.balanceAfter, false)}</span>
                            </Field>
                        )}
                        <Field label="Operação">{operationLabel}</Field>
                        <Field label="Identificador da operação">
                            <span className="font-mono text-xs">{entry.providerEventId}</span>
                        </Field>
                        <Field label="Descrição" wide>
                            <span>{entry.description || '—'}</span>
                        </Field>
                        <Field label="Registrado em">{formatDateTime(entry.createdAt)}</Field>
                    </div>
                </div>
            </div>

            {entry.billing && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <h2 className="card-title text-base">Cobrança vinculada</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <Field label="Cobrança">
                                <Link to={`/financial/billings/${entry.billing.id}`} className="link link-primary">
                                    #{entry.billing.id}
                                </Link>
                            </Field>
                            <Field label="Tipo">{entry.billing.type}</Field>
                            <Field label="Status">
                                <span className="badge badge-sm">{entry.billing.status}</span>
                            </Field>
                            <Field label="Método">{entry.billing.paymentMethod || '—'}</Field>
                            <Field label="Valor">
                                <span className="font-mono">{formatBrl(entry.billing.amount, false)}</span>
                            </Field>
                            <Field label="Valor pago">
                                <span className="font-mono">{entry.billing.paidAmount != null ? formatBrl(entry.billing.paidAmount, false) : '—'}</span>
                            </Field>
                            <Field label="Vencimento">{formatDateTime(entry.billing.dueDate)}</Field>
                            <Field label="Pago em">{formatDateTime(entry.billing.paidAt)}</Field>
                            <Field label="Descrição" wide>{entry.billing.description || '—'}</Field>
                            {entry.billing.providerPaymentId && (
                                <Field label="ID provedor" wide>
                                    <span className="font-mono text-xs">{entry.billing.providerPaymentId}</span>
                                </Field>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {entry.transfer && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <h2 className="card-title text-base">Transferência vinculada</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <Field label="Transferência">
                                <Link to={`/financial/transfers/${entry.transfer.id}`} className="link link-primary">
                                    #{entry.transfer.id}
                                </Link>
                            </Field>
                            <Field label="Tipo">{entry.transfer.kind}</Field>
                            <Field label="Status">
                                <span className="badge badge-sm">{entry.transfer.status}</span>
                            </Field>
                            <Field label="Valor">
                                <span className="font-mono">{formatBrl(entry.transfer.amount, false)}</span>
                            </Field>
                            <Field label="Executada em">{formatDateTime(entry.transfer.executedAt)}</Field>
                            {entry.transfer.failReason && (
                                <Field label="Motivo da falha" wide>{entry.transfer.failReason}</Field>
                            )}
                            <Field label="Descrição" wide>{entry.transfer.description || '—'}</Field>
                            {entry.transfer.providerTransferId && (
                                <Field label="ID provedor" wide>
                                    <span className="font-mono text-xs">{entry.transfer.providerTransferId}</span>
                                </Field>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {friendlyFields.length > 0 && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <h2 className="card-title text-base">Detalhes da operação</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mt-2">
                            {friendlyFields.map((f, i) => (
                                <Field key={`${f.label}-${i}`} label={f.label}>
                                    {f.value}
                                </Field>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

function Field({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
    return (
        <div className={wide ? 'md:col-span-2' : ''}>
            <p className="text-base-content/50 text-xs mb-0.5">{label}</p>
            <div>{children}</div>
        </div>
    )
}
